import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Heading, RyeconLineage, Sizes } from '@dbt-labs/sourdough';

import { Page } from '@/components/Page';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ResourceByEnvironmentParams } from '@/hooks/discovery/resource';
import { useLinkToExplorer } from '@/hooks/links';
import { useQueryParamOption } from '@/hooks/queryParamOption';
import { useAllProjects } from '@/hooks/useAllProjects';
import { ViewType, viewTypes } from '@/util/linkTypes';
import { buildTypePredicate } from '@/util/type';
import { ColumnsSidePanel } from './components/ColumnsSidePanel';
import { MultiProjectLineageView } from './components/MultiProjectLineageView';
import { PopularModelsSection } from './components/PopularModelsSection';
import { ProjectsSection } from './components/ProjectsSection';
import { RecentlyViewed } from './components/RecentlyViewed';

const _tabTypes = ['publicModels', 'projects'] as const;
export type TabType = (typeof _tabTypes)[number];

const isViewType = buildTypePredicate(viewTypes);
export const getViewType = (input: string | undefined | null): ViewType | null => {
  return isViewType(input) ? input : null;
};

export const NewMultiProjectPage = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const isRecentlyViewedEnabled = hasAccountFeature('explorerAccountUI');
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const searchResultsLink = useLinkToExplorer('summitSearchResults');

  const [_viewParam] = useQueryParamOption({
    param: 'view',
    options: viewTypes,
    validateOption: getViewType,
  });
  const viewParam: ViewType = _viewParam ?? 'list';

  const { accountId } = useDbtCloudAccount();
  const { data: allProjects, isLoading: isLoadingProjects } = useAllProjects(accountId);

  const [selectedResourceParams, setSelectedResourceParams] =
    useState<ResourceByEnvironmentParams>({
      environmentId: undefined,
      uniqueId: undefined,
    });
  const [isColumnsPanelVisible, setIsColumnsPanelVisible] = useState(false);
  if (!accountId) return null;
  return (
    <Page title="Account Overview" pageType="multiProject">
      <ColumnsSidePanel
        isVisible={isColumnsPanelVisible}
        setIsVisible={setIsColumnsPanelVisible}
        selectedResourceParams={selectedResourceParams}
      />
      <div className="flex h-full w-full flex-col bg-bgMain">
        {viewParam === 'lineage' && <MultiProjectLineageView />}
        {viewParam === 'list' && (
          <div className="flex w-full flex-col pb-12">
            <div className="w-full border-b border-borderMuted">
              <div className="mx-6 my-4 flex flex-row items-center justify-between">
                <div className="flex items-center">
                  <Heading size="3">Catalog</Heading>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <Button
                    size={Sizes.md}
                    type={'secondary'}
                    text={'Browse assets'}
                    onClick={() => {
                      navigate(
                        searchResultsLink({
                          accountId: accountId!,
                        }),
                      );
                    }}
                  />
                  <Button
                    size={Sizes.md}
                    ryecon={RyeconLineage}
                    onClick={() => {
                      searchParams.set('view', 'lineage');
                      setSearchParams(searchParams);
                    }}
                  >
                    Account lineage
                  </Button>
                </div>
              </div>
            </div>
            <div className="mx-6 mb-8 mt-4">
              {isRecentlyViewedEnabled && <RecentlyViewed />}
              <ProjectsSection
                allProjects={allProjects}
                isLoadingProjects={isLoadingProjects}
              />
              <PopularModelsSection
                setSelectedResourceParams={setSelectedResourceParams}
                setIsColumnsPanelVisible={setIsColumnsPanelVisible}
              />
            </div>
          </div>
        )}
      </div>
    </Page>
  );
};
